import { FormEvent, useRef, useState } from "react";

import useLabel from "@apps/use-label/use-label";
import Button from "@components/button/button";
import { ButtonSize, buttonTheme } from "@components/button/button-constants";
import Input, { InputLabel } from "@components/input/input";
import Modal from "@components/modal/modal";
import ModalTitle from "@components/modal/modal-title";
import { UserComboboxTeamOption } from "@components/user-combobox/user-combobox-list";

import DepartmentPickerButton from "../department-picker-button";

// Edits the track's name and department. The page saves as you go, so the
// change is written a moment after this closes.
const CareerTrackDetailsModal = ({
  organizationId,
  title,
  team,
  onClose,
  onChange,
  onDelete,
}: {
  organizationId: number;
  title: string;
  team: UserComboboxTeamOption | null;
  onClose: () => void;
  onChange: (details: {
    title: string;
    team: UserComboboxTeamOption | null;
  }) => void;
  // Set for a track that exists: deleting it asks first, wherever it's from.
  onDelete?: () => void;
}) => {
  const label = useLabel();
  const [proposedTitle, setProposedTitle] = useState(title);
  const [proposedTeam, setProposedTeam] =
    useState<UserComboboxTeamOption | null>(team);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const canApply = proposedTitle.trim().length > 0;

  const handleApply = (e?: FormEvent) => {
    e?.preventDefault();
    if (!canApply) return;
    onChange({ title: proposedTitle, team: proposedTeam });
    onClose();
  };

  return (
    <Modal
      open
      onClose={onClose}
      aria-label="Career track details modal"
      initialFocus={nameInputRef}
    >
      <div className="p-6 flex flex-col gap-6">
        <ModalTitle onClose={onClose}>Track Details</ModalTitle>
        <form className="flex flex-col gap-6" onSubmit={handleApply}>
          <Input
            ref={nameInputRef}
            autoFocus
            label="Name"
            aria-label="Career track title input"
            value={proposedTitle}
            onChange={(evt) => setProposedTitle(evt.target.value)}
          />
          <div>
            <InputLabel
              label={`Associated ${label("team")} (optional)`}
              className="mb-1"
            />
            <div className="flex items-center gap-2">
              <DepartmentPickerButton
                organizationId={organizationId}
                team={proposedTeam}
                ariaLabel="Career track department picker"
                onChange={setProposedTeam}
                size={ButtonSize.large}
                showDownChevronIcon
                wrapperClassName="flex-1"
                className="w-full justify-between font-normal"
              />
              {proposedTeam && (
                <Button
                  theme={buttonTheme.text}
                  size={ButtonSize.medium}
                  text="Clear"
                  onClick={() => setProposedTeam(null)}
                />
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {onDelete && (
              <Button
                theme={buttonTheme.redDanger}
                text="Delete Track"
                aria-label="Career track details: Delete track"
                onClick={() => {
                  onClose();
                  onDelete();
                }}
              />
            )}
            <div className="ml-auto flex items-center gap-2">
              <Button
                theme={buttonTheme.text}
                text="Cancel"
                onClick={onClose}
              />
              <Button
                type="submit"
                theme={buttonTheme.primary}
                disabled={!canApply}
                tooltip={canApply ? null : "Please enter a name"}
                text="Done"
              />
            </div>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default CareerTrackDetailsModal;
